/**
 * TL;DR: Smart product purchase interface with gift card and store availability features.
 * Features:
 * - Add to cart with state management
 * - Gift card recipient form handling
 * - Store availability integration
 * - Error handling with field validation
 * - Variant and quantity change support
 * - Accessibility enhancements (ARIA)
 */

import { d as addItemByForm, a as contexts } from './cart-66092e7d.js';
import { E as EVENTS } from './events-29317bb6.js';

class ProductBlockBuyButtons extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      productContainer: "main-product-default, [js-quick-view-content]",
      detailsContainer: ".product__details",
      storeAvailabilityDrawerTrigger: "[js-store-availability-drawer-trigger]",

      // Add to cart success and error
      addToCartButton: "[js-add-to-cart-button-wrapper]",
      addToCartError: "[js-add-to-cart-error]",
      giftCardRecipientForm: ".gift-card-recipient__fields",
      giftCardRecipientInput: ".gift-card-recipient__input",
      productForm: "[data-product-form]",
      showGiftCardRecipientCheckbox: "#gift-card-show-form",
    };

    this.classes = {
      hasError: "has-error",
      disabled: "disabled",
      hidden: "hidden",
      changePending: "change-pending",
    };

    this.strings = window.theme.strings;
  }

  connectedCallback() {
    this.controller = new AbortController();
    this.productContainer = this.closest(this.selectors.productContainer);
    this.detailsContainer = this.productContainer.querySelector(
      this.selectors.detailsContainer,
    );
    this.addToCartButton = this.querySelector(this.selectors.addToCartButton);

    // Listen for quantity change
    this.productContainer.addEventListener(
      EVENTS.QUANTITY_INPUT_CHANGE,
      () => {
        this.classList.remove(this.classes.hasError);
      },
      { signal: this.controller.signal },
    );

    // Listen for variant change
    this.addEventListener(
      EVENTS.PRODUCT_VARIANT_CHANGED,
      this.handleRepaint.bind(this),
      { signal: this.controller.signal },
    );

    this.initContentListeners();
  }

  //
  // General setup functions
  //
  initContentListeners() {
    this.contentListenersController = new AbortController();

    // Check gift card recipient form setting and gift card product
    this.enableGiftCardRecipientForm =
      this.getAttribute("data-enable-gift-card-recipient-form") === "true";

    this.showGiftCardRecipientCheckbox = this.querySelector(
      this.selectors.showGiftCardRecipientCheckbox,
    );
    if (
      this.enableGiftCardRecipientForm &&
      this.showGiftCardRecipientCheckbox
    ) {
      this.setupGiftCardRecipientForm();
    }

    // Listen for Store availability drawer click
    this.storeAvailabilityDrawerTrigger = this.querySelector(
      this.selectors.storeAvailabilityDrawerTrigger,
    );

    if (this.storeAvailabilityDrawerTrigger) {
      this.drawerId =
        this.storeAvailabilityDrawerTrigger.getAttribute("data-drawer-id");

      this.storeAvailabilityDrawerTrigger.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          window.dispatchEvent(
            new CustomEvent(
              EVENTS.MODAL_STORE_AVAILABILITY_DRAWER_OPEN(this.drawerId),
            ),
          );
        },
        { signal: this.contentListenersController.signal },
      );
    }

    // Listen for Add to cart submit
    this.productForm = this.querySelector(this.selectors.productForm);
    this.productForm?.addEventListener(
      "submit",
      (event) => {
        this.handleFormSubmission(event);
      },
      { signal: this.contentListenersController.signal },
    );

    // Listen for Add to cart error at product container level
    this.addToCartError = this.querySelector(this.selectors.addToCartError);
    this.addEventListener(
      EVENTS.PRODUCT_ADD_ERROR,
      this.handleAddToCartError.bind(this),
      { signal: this.contentListenersController.signal },
    );

    // Listen for successfull add to cart
    this.addEventListener(
      EVENTS.PRODUCT_ADD_SUCCESS,
      () => {
        this.classList.remove(this.classes.hasError);
        this.resetGiftCardRecipientForm();
        this.handleAddToCartSuccess();
      },
      { signal: this.contentListenersController.signal },
    );
  }

  setupGiftCardRecipientForm() {
    this.giftCardRecipientForm = this.querySelector(
      this.selectors.giftCardRecipientForm,
    );
    this.giftCardRecipientInputs = this.querySelectorAll(
      this.selectors.giftCardRecipientInput,
    );

    // Set default form visibility
    this.showGiftCardRecipientCheckbox.checked = false;
    this.giftCardRecipientForm.style.display = "none";

    // Listen for checked 'show form' input
    this.showGiftCardRecipientCheckbox.addEventListener(
      "change",
      () => {
        if (this.showGiftCardRecipientCheckbox.checked) {
          this.giftCardRecipientForm.style.display = "block";
        } else {
          // reset required to 'disable' the form's requirements
          this.resetGiftCardRecipientForm();
        }
      },
      { signal: this.controller.signal },
    );
  }

  resetGiftCardRecipientForm() {
    if (!this.giftCardRecipientInputs) {
      return
    }

    this.giftCardRecipientInputs.forEach((input) => {
      input.value = "";
      input.classList.remove("has-error");
    });

    this.showGiftCardRecipientCheckbox.checked = false;
    this.giftCardRecipientForm.style.display = "none";
  }

  //
  // Variant and quantity change handling functions
  //
  handleRepaint() {
    this.contentListenersController.abort();
    this.addToCartButton = this.querySelector(this.selectors.addToCartButton);
    this.initContentListeners();
  }

  //
  // Add to cart and results handling functions
  //
  handleFormSubmission(event) {
    event.preventDefault();

    this.addToCartButton.dispatchEvent(
      new CustomEvent(EVENTS.BUTTON_STATE_STARTED),
    );

    this.detailsContainer.classList.add(this.classes.changePending);
    addItemByForm(this.productForm, contexts.productPage, this);
  }

  handleAddToCartSuccess() {
    this.detailsContainer.classList.remove(this.classes.changePending);
    this.addToCartButton.dispatchEvent(
      new CustomEvent(EVENTS.BUTTON_STATE_COMPLETED),
    );

    this.productContainer.dispatchEvent(
      new CustomEvent(EVENTS.PRODUCT_ADD_SUCCESS),
    );
  }

  handleAddToCartError(event) {
    this.detailsContainer.classList.remove(this.classes.changePending);
    this.addToCartButton.dispatchEvent(
      new CustomEvent(EVENTS.BUTTON_STATE_INCOMPLETED),
    );

    const formErrors = event.detail.errorMessage.errors;
    this.classList.add(this.classes.hasError);

    // Gift card recipient errors can come with better error context
    if (formErrors) {
      const sectionId = this.querySelector(
        this.selectors.giftCardRecipientForm,
      ).getAttribute("data-section-id");
      this.addToCartError.innerText = "";

      Object.entries(formErrors).forEach(([key]) => {
        // Update error field visual and aria
        const errorMessageID = `gift-card-recipient-${key}-error--${sectionId}`;
        const errorInput = this.querySelector(
          `#gift-card-recipient-${key}--${sectionId}`,
        );
        errorInput.classList.add(this.classes.hasError);
        errorInput.setAttribute("aria-invalid", true);
        errorInput.setAttribute("aria-describedby", errorMessageID);

        // List errors together above add to cart button
        this.addToCartError.innerText +=
          `${this.strings.products.giftCardRecipientErrors[key]}` + "\n";
      });
    } else {
      this.addToCartError.innerText = event.detail.errorMessage.description;
    }
  }

  disconnectedCallback() {
    this.controller.abort();
  }
}

if (!customElements.get("product-blocks-buy-buttons")) {
  customElements.define("product-block-buy-buttons", ProductBlockBuyButtons);
}
